/**
 * Configuration des permissions par rôle pour le système POS
 * Restaurant DAMAS - Système de caisse professionnel
 * 
 * Hiérarchie des rôles (du plus élevé au plus bas):
 * 1. ADMIN - Accès total
 * 2. DIRECTEUR - Gestion complète sans accès système
 * 3. DIRECTEUR_ADJOINT - Gestion opérationnelle
 * 4. CAISSIER - Encaissements et commandes
 * 5. SERVEUR/SERVEUSE - Prise de commandes
 * 6. BARMAN - Commandes boissons uniquement
 * 7. CUISINIER - Visualisation cuisine
 */

// Types de permissions
export const PERMISSION_TYPES = {
    // Gestion des caisses
    REGISTER_OPEN: 'register_open',
    REGISTER_CLOSE: 'register_close',
    REGISTER_FORCE_CLOSE: 'register_force_close',
    REGISTER_VIEW_ALL: 'register_view_all',
    REGISTER_TRANSFER: 'register_transfer',
    
    // Commandes
    ORDER_CREATE: 'order_create',
    ORDER_MODIFY: 'order_modify',
    ORDER_CANCEL: 'order_cancel',
    ORDER_DELETE: 'order_delete',
    ORDER_VIEW_ALL: 'order_view_all',
    ORDER_REASSIGN: 'order_reassign',
    
    // Paiements
    PAYMENT_CASH: 'payment_cash',
    PAYMENT_CARD: 'payment_card',
    PAYMENT_MOBILE: 'payment_mobile',
    PAYMENT_DISCOUNT: 'payment_discount',
    PAYMENT_VOID: 'payment_void',
    PAYMENT_REFUND: 'payment_refund',
    
    // Produits et stock
    PRODUCT_VIEW: 'product_view',
    PRODUCT_ADD: 'product_add',
    PRODUCT_EDIT: 'product_edit',
    PRODUCT_DELETE: 'product_delete',
    STOCK_VIEW: 'stock_view',
    STOCK_ADJUST: 'stock_adjust',
    
    // Tables
    TABLE_VIEW: 'table_view',
    TABLE_ASSIGN: 'table_assign',
    TABLE_TRANSFER: 'table_transfer',
    TABLE_VIEW_ALL: 'table_view_all',
    
    // Cuisine et Bar
    KITCHEN_VIEW: 'kitchen_view',
    KITCHEN_MANAGE: 'kitchen_manage',
    BAR_VIEW: 'bar_view',
    BAR_MANAGE: 'bar_manage',
    
    // Rapports et clôtures
    REPORT_VIEW: 'report_view',
    REPORT_EXPORT: 'report_export',
    CLOSING_VALIDATE: 'closing_validate',
    CLOSING_CORRECT: 'closing_correct',
    
    // Administration
    USER_VIEW: 'user_view',
    USER_MANAGE: 'user_manage',
    SETTINGS_VIEW: 'settings_view',
    SETTINGS_EDIT: 'settings_edit',
    SUPERVISION: 'supervision',
};

// Configuration des permissions par rôle
export const ROLE_PERMISSIONS = {
    // ============================================
    // ADMIN - Accès total à tout le système
    // ============================================
    ADMIN: {
        label: 'Administrateur',
        description: 'Accès total à toutes les fonctionnalités',
        level: 100,
        color: '#7C3AED', // Purple
        icon: 'Crown',
        permissions: Object.values(PERMISSION_TYPES), // Toutes les permissions
        restrictions: [],
        specialAccess: {
            canAccessAllRegisters: true,
            canAccessAllTables: true,
            canOverrideClosings: true,
            canDeleteOrders: true,
            canModifyPastOrders: true,
            canForceOperations: true,
            canAccessSystemSettings: true,
        }
    },
    
    // ============================================
    // DIRECTEUR - Gestion complète sans accès système
    // ============================================
    DIRECTEUR: {
        label: 'Directeur',
        description: 'Gestion complète des opérations',
        level: 90,
        color: '#2563EB', // Blue
        icon: 'UserCog',
        permissions: [
            // Caisses
            PERMISSION_TYPES.REGISTER_OPEN,
            PERMISSION_TYPES.REGISTER_CLOSE,
            PERMISSION_TYPES.REGISTER_FORCE_CLOSE,
            PERMISSION_TYPES.REGISTER_VIEW_ALL,
            PERMISSION_TYPES.REGISTER_TRANSFER,
            // Commandes
            PERMISSION_TYPES.ORDER_CREATE,
            PERMISSION_TYPES.ORDER_MODIFY,
            PERMISSION_TYPES.ORDER_CANCEL,
            PERMISSION_TYPES.ORDER_VIEW_ALL,
            PERMISSION_TYPES.ORDER_REASSIGN,
            // Paiements
            PERMISSION_TYPES.PAYMENT_CASH,
            PERMISSION_TYPES.PAYMENT_CARD,
            PERMISSION_TYPES.PAYMENT_MOBILE,
            PERMISSION_TYPES.PAYMENT_DISCOUNT,
            PERMISSION_TYPES.PAYMENT_VOID,
            PERMISSION_TYPES.PAYMENT_REFUND,
            // Produits
            PERMISSION_TYPES.PRODUCT_VIEW,
            PERMISSION_TYPES.PRODUCT_ADD,
            PERMISSION_TYPES.PRODUCT_EDIT,
            PERMISSION_TYPES.STOCK_VIEW,
            PERMISSION_TYPES.STOCK_ADJUST,
            // Tables
            PERMISSION_TYPES.TABLE_VIEW,
            PERMISSION_TYPES.TABLE_ASSIGN,
            PERMISSION_TYPES.TABLE_TRANSFER,
            PERMISSION_TYPES.TABLE_VIEW_ALL,
            // Cuisine/Bar
            PERMISSION_TYPES.KITCHEN_VIEW,
            PERMISSION_TYPES.KITCHEN_MANAGE,
            PERMISSION_TYPES.BAR_VIEW,
            PERMISSION_TYPES.BAR_MANAGE,
            // Rapports
            PERMISSION_TYPES.REPORT_VIEW,
            PERMISSION_TYPES.REPORT_EXPORT,
            PERMISSION_TYPES.CLOSING_VALIDATE,
            PERMISSION_TYPES.CLOSING_CORRECT,
            // Users
            PERMISSION_TYPES.USER_VIEW,
            PERMISSION_TYPES.SUPERVISION,
        ],
        restrictions: [
            'Pas d\'accès aux paramètres système',
            'Pas de suppression de commandes anciennes',
        ],
        specialAccess: {
            canAccessAllRegisters: true,
            canAccessAllTables: true,
            canOverrideClosings: true,
            canDeleteOrders: false,
            canModifyPastOrders: false,
            canForceOperations: true,
            canAccessSystemSettings: false,
        }
    },
    
    // ============================================
    // DIRECTEUR_ADJOINT - Gestion opérationnelle
    // ============================================
    DIRECTEUR_ADJOINT: {
        label: 'Directeur Adjoint',
        description: 'Gestion opérationnelle du service',
        level: 80,
        color: '#0891B2', // Cyan
        icon: 'UserCheck',
        permissions: [
            // Caisses
            PERMISSION_TYPES.REGISTER_OPEN,
            PERMISSION_TYPES.REGISTER_CLOSE,
            PERMISSION_TYPES.REGISTER_VIEW_ALL,
            // Commandes
            PERMISSION_TYPES.ORDER_CREATE,
            PERMISSION_TYPES.ORDER_MODIFY,
            PERMISSION_TYPES.ORDER_CANCEL,
            PERMISSION_TYPES.ORDER_VIEW_ALL,
            PERMISSION_TYPES.ORDER_REASSIGN,
            // Paiements
            PERMISSION_TYPES.PAYMENT_CASH,
            PERMISSION_TYPES.PAYMENT_CARD,
            PERMISSION_TYPES.PAYMENT_MOBILE,
            PERMISSION_TYPES.PAYMENT_DISCOUNT,
            // Produits
            PERMISSION_TYPES.PRODUCT_VIEW,
            PERMISSION_TYPES.STOCK_VIEW,
            // Tables
            PERMISSION_TYPES.TABLE_VIEW,
            PERMISSION_TYPES.TABLE_ASSIGN,
            PERMISSION_TYPES.TABLE_TRANSFER,
            PERMISSION_TYPES.TABLE_VIEW_ALL,
            // Cuisine/Bar
            PERMISSION_TYPES.KITCHEN_VIEW,
            PERMISSION_TYPES.KITCHEN_MANAGE,
            PERMISSION_TYPES.BAR_VIEW,
            PERMISSION_TYPES.BAR_MANAGE,
            // Rapports
            PERMISSION_TYPES.REPORT_VIEW,
            PERMISSION_TYPES.CLOSING_VALIDATE,
            PERMISSION_TYPES.SUPERVISION,
        ],
        restrictions: [
            'Pas de fermeture forcée des caisses',
            'Pas de remboursements',
            'Pas de modification des produits',
        ],
        specialAccess: {
            canAccessAllRegisters: true,
            canAccessAllTables: true,
            canOverrideClosings: false,
            canDeleteOrders: false,
            canModifyPastOrders: false,
            canForceOperations: false,
            canAccessSystemSettings: false,
        }
    },
    
    // ============================================
    // CAISSIER - Encaissements et commandes
    // ============================================
    CAISSIER: {
        label: 'Caissier(e)',
        description: 'Gestion de la caisse et des encaissements',
        level: 60,
        color: '#16A34A', // Green
        icon: 'CreditCard',
        permissions: [
            // Caisses (limité à sa caisse)
            PERMISSION_TYPES.REGISTER_OPEN,
            PERMISSION_TYPES.REGISTER_CLOSE,
            // Commandes
            PERMISSION_TYPES.ORDER_CREATE,
            PERMISSION_TYPES.ORDER_MODIFY,
            // Paiements
            PERMISSION_TYPES.PAYMENT_CASH,
            PERMISSION_TYPES.PAYMENT_CARD,
            PERMISSION_TYPES.PAYMENT_MOBILE,
            // Produits
            PERMISSION_TYPES.PRODUCT_VIEW,
            PERMISSION_TYPES.STOCK_VIEW,
            // Tables
            PERMISSION_TYPES.TABLE_VIEW,
            PERMISSION_TYPES.TABLE_ASSIGN,
        ],
        restrictions: [
            'Accès uniquement à sa caisse assignée',
            'Pas de remises sans validation',
            'Pas d\'annulation de commandes',
        ],
        specialAccess: {
            canAccessAllRegisters: false,
            canAccessAllTables: false,
            canOverrideClosings: false,
            canDeleteOrders: false,
            canModifyPastOrders: false,
            canForceOperations: false,
            canAccessSystemSettings: false,
        }
    },
    
    // ============================================
    // SERVEUR/SERVEUSE - Prise de commandes
    // ============================================
    SERVEUR: {
        label: 'Serveur(se)',
        description: 'Prise de commandes et service en salle',
        level: 40,
        color: '#EA580C', // Orange
        icon: 'Utensils',
        permissions: [
            // Commandes (ses tables uniquement)
            PERMISSION_TYPES.ORDER_CREATE,
            PERMISSION_TYPES.ORDER_MODIFY,
            // Paiements (limités)
            PERMISSION_TYPES.PAYMENT_CASH,
            // Produits
            PERMISSION_TYPES.PRODUCT_VIEW,
            // Tables (ses tables)
            PERMISSION_TYPES.TABLE_VIEW,
            PERMISSION_TYPES.TABLE_ASSIGN,
        ],
        restrictions: [
            'Accès uniquement à ses tables assignées',
            'Encaissement espèces uniquement',
            'Pas d\'accès à la caisse',
            'Pas de modification des commandes envoyées',
        ],
        specialAccess: {
            canAccessAllRegisters: false,
            canAccessAllTables: false,
            canOverrideClosings: false,
            canDeleteOrders: false,
            canModifyPastOrders: false,
            canForceOperations: false,
            canAccessSystemSettings: false,
            tablesOnly: true, // Seulement ses tables
        }
    },
    
    // ============================================
    // BARMAN - Commandes boissons uniquement
    // ============================================
    BARMAN: {
        label: 'Barman',
        description: 'Gestion des commandes du bar',
        level: 50,
        color: '#7C3AED', // Purple
        icon: 'Wine',
        permissions: [
            // Commandes (bar seulement)
            PERMISSION_TYPES.ORDER_CREATE,
            PERMISSION_TYPES.ORDER_MODIFY,
            // Paiements
            PERMISSION_TYPES.PAYMENT_CASH,
            PERMISSION_TYPES.PAYMENT_CARD,
            PERMISSION_TYPES.PAYMENT_MOBILE,
            // Produits (boissons)
            PERMISSION_TYPES.PRODUCT_VIEW,
            PERMISSION_TYPES.STOCK_VIEW,
            // Bar
            PERMISSION_TYPES.BAR_VIEW,
            PERMISSION_TYPES.BAR_MANAGE,
        ],
        restrictions: [
            'Accès uniquement à la Caisse Bar',
            'Catégories boissons uniquement',
            'Pas d\'accès aux commandes cuisine',
        ],
        specialAccess: {
            canAccessAllRegisters: false,
            canAccessAllTables: false,
            canOverrideClosings: false,
            canDeleteOrders: false,
            canModifyPastOrders: false,
            canForceOperations: false,
            canAccessSystemSettings: false,
            barOnly: true, // Seulement le bar
            allowedCategories: ['Boissons', 'Boissons fraîches', 'Cocktails', 'Vins', 'Bières'],
        }
    },
    
    // ============================================
    // CUISINIER - Visualisation cuisine
    // ============================================
    CUISINIER: {
        label: 'Cuisinier',
        description: 'Gestion des commandes en cuisine',
        level: 30,
        color: '#DC2626', // Red
        icon: 'ChefHat',
        permissions: [
            // Cuisine
            PERMISSION_TYPES.KITCHEN_VIEW,
            PERMISSION_TYPES.KITCHEN_MANAGE,
            // Produits (lecture)
            PERMISSION_TYPES.PRODUCT_VIEW,
            PERMISSION_TYPES.STOCK_VIEW,
        ],
        restrictions: [
            'Pas d\'accès aux caisses',
            'Pas d\'encaissements',
            'Pas de prise de commandes',
            'Visualisation et préparation uniquement',
        ],
        specialAccess: {
            canAccessAllRegisters: false,
            canAccessAllTables: false,
            canOverrideClosings: false,
            canDeleteOrders: false,
            canModifyPastOrders: false,
            canForceOperations: false,
            canAccessSystemSettings: false,
            kitchenOnly: true,
        }
    },
};

// Alias pour compatibilité
export const ROLE_ALIASES = {
    'SERVEUSE': 'SERVEUR',
    'SERVER': 'SERVEUR',
    'WAITER': 'SERVEUR',
    'WAITRESS': 'SERVEUR',
    'CASHIER': 'CAISSIER',
    'BARTENDER': 'BARMAN',
    'COOK': 'CUISINIER',
    'CHEF': 'CUISINIER',
    'MANAGER': 'DIRECTEUR',
    'ASSISTANT_MANAGER': 'DIRECTEUR_ADJOINT',
};

/**
 * Vérifie si un rôle a une permission spécifique
 */
export const hasPermission = (userRole, permission) => {
    if (!userRole || !permission) return false;
    
    // Résoudre les alias
    const resolvedRole = ROLE_ALIASES[userRole.toUpperCase()] || userRole.toUpperCase();
    const roleConfig = ROLE_PERMISSIONS[resolvedRole];
    
    if (!roleConfig) return false;
    
    return roleConfig.permissions.includes(permission);
};

/**
 * Vérifie si un rôle a un niveau supérieur ou égal à un autre
 */
export const hasRoleLevel = (userRole, requiredLevel) => {
    if (!userRole) return false;
    
    const resolvedRole = ROLE_ALIASES[userRole.toUpperCase()] || userRole.toUpperCase();
    const roleConfig = ROLE_PERMISSIONS[resolvedRole];
    
    if (!roleConfig) return false;
    
    return roleConfig.level >= requiredLevel;
};

/**
 * Obtient la configuration complète d'un rôle
 */
export const getRoleConfig = (userRole) => {
    if (!userRole) return null;
    
    const resolvedRole = ROLE_ALIASES[userRole.toUpperCase()] || userRole.toUpperCase();
    return ROLE_PERMISSIONS[resolvedRole] || null;
};

/**
 * Vérifie si un utilisateur peut accéder à une caisse spécifique
 */
export const canAccessRegister = (user, register) => {
    if (!user || !register) return false;
    
    const roleConfig = getRoleConfig(user.role);
    if (!roleConfig) return false;
    
    // Admin et Directeurs peuvent accéder à toutes les caisses
    if (roleConfig.specialAccess?.canAccessAllRegisters) return true;
    
    // Barman peut accéder uniquement à la Caisse Bar
    if (roleConfig.specialAccess?.barOnly) {
        return register.type === 'bar' || register.name?.toLowerCase().includes('bar');
    }
    
    // Cuisinier n'a pas accès aux caisses
    if (roleConfig.specialAccess?.kitchenOnly) return false;
    
    // Autres utilisateurs: vérifier l'assignation
    return register.assigned_user_id === user.id;
};

/**
 * Vérifie si un utilisateur peut voir une table
 */
export const canAccessTable = (user, table) => {
    if (!user || !table) return false;
    
    const roleConfig = getRoleConfig(user.role);
    if (!roleConfig) return false;
    
    // Admin et Directeurs peuvent voir toutes les tables
    if (roleConfig.specialAccess?.canAccessAllTables) return true;
    
    // Serveurs voient uniquement leurs tables
    if (roleConfig.specialAccess?.tablesOnly) {
        return table.server_id === user.id || table.assigned_to === user.id;
    }
    
    return hasPermission(user.role, PERMISSION_TYPES.TABLE_VIEW);
};

/**
 * Filtre les catégories autorisées pour un rôle
 */
export const getAllowedCategories = (userRole, allCategories) => {
    const roleConfig = getRoleConfig(userRole);
    if (!roleConfig) return [];
    
    // Si pas de restriction de catégories
    if (!roleConfig.specialAccess?.allowedCategories) return allCategories;
    
    const allowed = roleConfig.specialAccess.allowedCategories.map(c => c.toLowerCase());
    return allCategories.filter(cat => 
        allowed.some(a => cat.name?.toLowerCase().includes(a) || cat.toLowerCase?.().includes(a))
    );
};

/**
 * Obtient le résumé des permissions pour l'UI
 */
export const getPermissionsSummary = (userRole) => {
    const roleConfig = getRoleConfig(userRole);
    if (!roleConfig) return { canDo: [], cantDo: [] };
    
    const canDo = [];
    const cantDo = [];
    
    // Caisses
    if (hasPermission(userRole, PERMISSION_TYPES.REGISTER_VIEW_ALL)) {
        canDo.push('Voir toutes les caisses');
    }
    if (hasPermission(userRole, PERMISSION_TYPES.REGISTER_OPEN)) {
        canDo.push('Ouvrir une caisse');
    }
    if (hasPermission(userRole, PERMISSION_TYPES.REGISTER_FORCE_CLOSE)) {
        canDo.push('Fermeture forcée');
    }
    
    // Commandes
    if (hasPermission(userRole, PERMISSION_TYPES.ORDER_CREATE)) {
        canDo.push('Prendre des commandes');
    }
    if (hasPermission(userRole, PERMISSION_TYPES.ORDER_CANCEL)) {
        canDo.push('Annuler des commandes');
    } else {
        cantDo.push('Annuler des commandes');
    }
    if (hasPermission(userRole, PERMISSION_TYPES.ORDER_DELETE)) {
        canDo.push('Supprimer des commandes');
    } else {
        cantDo.push('Supprimer des commandes');
    }
    
    // Paiements
    if (hasPermission(userRole, PERMISSION_TYPES.PAYMENT_REFUND)) {
        canDo.push('Effectuer des remboursements');
    } else {
        cantDo.push('Effectuer des remboursements');
    }
    if (hasPermission(userRole, PERMISSION_TYPES.PAYMENT_DISCOUNT)) {
        canDo.push('Appliquer des remises');
    }
    
    // Rapports
    if (hasPermission(userRole, PERMISSION_TYPES.REPORT_VIEW)) {
        canDo.push('Voir les rapports');
    }
    if (hasPermission(userRole, PERMISSION_TYPES.CLOSING_VALIDATE)) {
        canDo.push('Valider les clôtures');
    }
    
    return { canDo, cantDo, restrictions: roleConfig.restrictions || [] };
};

export default {
    PERMISSION_TYPES,
    ROLE_PERMISSIONS,
    ROLE_ALIASES,
    hasPermission,
    hasRoleLevel,
    getRoleConfig,
    canAccessRegister,
    canAccessTable,
    getAllowedCategories,
    getPermissionsSummary,
};
